'use client'

import { useComponentTranslation } from '@core/i18n'
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Icon,
  Tag,
  Text,
} from '@core/ui-elements'
import { ICheckingItem, IPickingProduct } from '@data/types'
import { removeLastHyphen } from '@utils/formatters'
import { forwardRef, HTMLAttributes, PropsWithChildren } from 'react'
import { FiEdit } from 'react-icons/fi'
import { MdLocationOn } from 'react-icons/md'

import { FallbackImageError } from '../fallback-image-error'
import { LastCharactersStrong } from '../last-characters-strong'

export interface IResumeProductCard {
  product: IPickingProduct | ICheckingItem
  showLocation?: boolean
  handleClick?: () => void
  viewOnly?: boolean
}

type ResumeProductCardTagsProps = {
  product: IPickingProduct | ICheckingItem
  isCurrentItem?: boolean
}

ResumeProductCard.Tags = forwardRef<
  HTMLDivElement,
  PropsWithChildren<ResumeProductCardTagsProps & HTMLAttributes<HTMLDivElement>>
>(function Tags({ product, isCurrentItem = false, children }, ref) {
  return (
    <Flex
      ref={ref}
      backgroundColor={
        isCurrentItem ? 'orange.200' : product.productStatusColor.secondary
      }
      borderRadius={'0 0 0.5rem 0.5rem'}
      gap={'0.25rem'}
      justifyContent={'center'}
      padding={'0.5rem'}
      wrap={'wrap'}
    >
      {children}
    </Flex>
  )
})

export function ResumeProductCard({
  children,
  product,
  showLocation = false,
  handleClick,
  viewOnly = false,
}: PropsWithChildren<IResumeProductCard>) {
  const t = useComponentTranslation('ResumeProductCard')

  return (
    <Card
      borderRadius={'0.5rem'}
      data-testid={'resume-product-card'}
      width={'100%'}
      onClick={handleClick}
    >
      <CardBody padding={'0.5rem'} width={'100%'}>
        <Flex align="center" gap={'0.25rem'} justify="space-between">
          <Box
            borderRadius={'0.25rem'}
            height={'3.75rem'}
            margin="0"
            minWidth={'3.75rem'}
            padding="0"
          >
            <FallbackImageError
              alt={product?.name ?? ''}
              height={'100%'}
              iconSize="3.15rem"
              imageUrl={product.imageUrl}
            />
          </Box>
          <Box overflow={'hidden'} width="100%">
            {showLocation && (
              <Tag size="xs">
                <Icon as={MdLocationOn} /> {removeLastHyphen(product.location)}
              </Tag>
            )}
            <Text
              fontWeight={'600'}
              overflow={'hidden'}
              textOverflow={'ellipsis'}
              whiteSpace={'nowrap'}
            >
              {product.name}
            </Text>
            <Text color={'gray.600'} fontSize={'0.75rem'}>
              <LastCharactersStrong
                color="var(--chakra-colors-blue-500)"
                label={t('Code', {
                  code: product?.barcodes[0],
                })}
              />
            </Text>
          </Box>

          {!viewOnly && !product.isPending ? (
            <Button aria-label={t('Edit item')} variant="ghost">
              <Icon as={FiEdit} color="blue.500" />
            </Button>
          ) : null}
        </Flex>
      </CardBody>

      {children}
    </Card>
  )
}
